import { useLocation } from "wouter";
import {
  Text,
  Paper,
  Container,
  Title,
  Stack,
  TextInput,
  Group,
  Button,
  Divider,
} from "@mantine/core";
// import { RedditButton } from "./RedditButton";
import { GoogleButton } from "./GoogleButton";
import { useStore } from "../store";
import mixpanel from "../tracking";
import { useForm } from "@mantine/form";
import { useEffect, useRef, useState } from "react";
import { notifications } from "@mantine/notifications";
import { requestLogin, installPluginFromUrl, getTurnstileSiteKey } from "../api";
import TurnstileField from "./TurnstileField";

export default function AuthenticationForm(props) {
  const [, setLocation] = useLocation();
  const authToken = useStore((state) => state.authToken);
  const setAuthToken = useStore((state) => state.setAuthToken);
  const pluginData = useStore((state) => state.pluginData);
  const setPluginData = useStore((state) => state.setPluginData);

  // Get plugin URL from query params
  const urlParams = new URLSearchParams(window.location.search);
  const pluginUrl = urlParams.get('plugin');

  function openWindow(url) {
    const width = 450;
    const height = 730;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    const bc = new BroadcastChannel("vortexauth");
    bc.onmessage = async (event) => {
      // Backend signals a blocked sign-in (e.g. cross-provider collision) by
      // broadcasting an error and an empty token; surface that to the user
      // instead of silently storing an empty session.
      if (event.data?.error) {
        notifications.show({
          title: "Sign-in blocked",
          message: event.data.error,
          color: "red",
          autoClose: 8000,
        });
        mixpanel.track("Sign-In Blocked", { reason: event.data.error });
        return;
      }
      if (!event.data?.token) {
        notifications.show({
          title: "Sign-in failed",
          message: "Something went wrong during sign-in. Please try again.",
          color: "red",
        });
        return;
      }
      setAuthToken(event.data.token);
      
      // Check if there's a plugin URL to auto-install
      if (event.data.pluginUrl && event.data.pluginUrl !== '') {
        try {
          notifications.show({
            id: 'plugin-install',
            title: "Installing plugin...",
            message: "Please wait while we install the plugin",
            loading: true,
            autoClose: false,
          });
          
          const plugin = await installPluginFromUrl(event.data.pluginUrl);
          setPluginData([...(pluginData ?? []), plugin]);
          
          notifications.update({
            id: 'plugin-install',
            title: "Plugin installed!",
            message: "The plugin has been successfully installed",
            color: "green",
            loading: false,
            autoClose: 3000,
          });
          
          mixpanel.track("Plugin Auto-Installed", { url: event.data.pluginUrl });
        } catch (err) {
          notifications.update({
            id: 'plugin-install',
            title: "Plugin installation failed",
            message: "Could not install the plugin automatically. You can upload it manually from the plugins page.",
            color: "red",
            loading: false,
            autoClose: 5000,
          });
          mixpanel.track("Plugin Auto-Install Failed", { error: err.message });
        }
      }
      
      setLocation("/");
      mixpanel.track("Sign-In Succeeded");
    };
    window.open(
      url,
      "_blank",
      `popup,menubar=no,location=no,resizable=no,scrollbars=no,status=no, width=${width}, height=${height}, top=${top}, left=${left}`
    );
  }

  const [isFetching, setIsFetching] = useState(false);
  const [siteKey, setSiteKey] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    getTurnstileSiteKey()
      .then((data) => {
        if (!cancelled) setSiteKey(data.siteKey);
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileError(
            "Email sign-in is unavailable right now. Use Google, or try again later.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      email: "",
    },
    validate: {
      email: (val) => (/^\S+@\S+$/.test(val) ? null : "Invalid email"),
    },
  });

  if (authToken) {
    return (
      <Container size={420} mt="xl">
        <Paper radius="md" p="xl" withBorder {...props}>
          <Text size="lg" fw={500}>
            You are logged in
          </Text>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size={420} mt="xl">
      <Paper radius="md" p="xl" withBorder {...props}>
        <Title order={2}>Start taking pictures!</Title>
        {pluginUrl && (
          <Text size="sm" c="dimmed" mt="sm">
            A plugin will be automatically installed after you sign in.
          </Text>
        )}
        {/* 
        <Text size="md" mt="xl" fw={500}>
          Choose a login method
        </Text> */}

        <Stack mb="md" mt="md">
          <GoogleButton
            type="submit"
            radius="xl"
            onClick={() => {
              const url = pluginUrl 
                ? `/api/auth/signin?provider=Google&pluginUrl=${encodeURIComponent(pluginUrl)}`
                : "/api/auth/signin?provider=Google";
              openWindow(url);
              mixpanel.track("Sign-In Requested", { Provider: "Google", hasPlugin: !!pluginUrl });
            }}
          >
            Google
          </GoogleButton>

          <Divider orientation="horizontal" label="or" />

          <form
            onSubmit={form.onSubmit((values) => {
              setIsFetching(true);
              requestLogin({ ...values, turnstileToken })
                .then(() => {
                  setIsFetching(false);
                  turnstileRef.current?.reset();
                  notifications.show({
                    title: "Email sent",
                    message: "Please follow the link in the email",
                    color: "green",
                    autoClose: false,
                  });
                })
                .catch((err) => {
                  setIsFetching(false);
                  turnstileRef.current?.reset();
                  notifications.show({
                    title: "An error occurred!",
                    message: "Sorry, something went wrong: " + err,
                    color: "red",
                    autoClose: false,
                  });
                });
            })}
          >
            <Stack gap="sm">
              <TextInput
                placeholder="Enter your email"
                {...form.getInputProps("email")}
                key={form.key("email")}
              />
              {siteKey && (
                <TurnstileField
                  ref={turnstileRef}
                  siteKey={siteKey}
                  onToken={setTurnstileToken}
                  onError={() =>
                    setTurnstileError(
                      "Could not load the verification check. Refresh the page and try again.",
                    )
                  }
                />
              )}
              {turnstileError && (
                <Text size="sm" c="red">
                  {turnstileError}
                </Text>
              )}
              <Group justify="flex-end">
                <Button
                  loading={isFetching}
                  variant="gradient"
                  type="submit"
                  disabled={!turnstileToken}
                >
                  Proceed
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
